export type Canvas2DContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface WordSegment {
  text: string;
  color?: string;
  scale?: number;
}

/** Measures a line's total rendered width, accounting for manual letter spacing. */
export function measureLineWidth(ctx: Canvas2DContext, text: string, letterSpacingPx: number): number {
  let width = 0;
  for (const char of text) width += ctx.measureText(char).width + letterSpacingPx;
  return Math.max(0, width - letterSpacingPx);
}

/**
 * Draws one line of text word-by-word so each word can have its own color/scale
 * (needed for word-highlight / current-word-emphasis caption animations), with
 * manual letter-spacing (for cross-browser consistency; native `ctx.letterSpacing`
 * support varies) and an optional stroke outline.
 */
export function drawLineWithWords(
  ctx: Canvas2DContext,
  words: WordSegment[],
  boxLeft: number,
  boxRight: number,
  baselineY: number,
  align: 'left' | 'center' | 'right',
  letterSpacingPx: number,
  outlineColor: string,
  outlineWidthPx: number,
  defaultColor: string
): void {
  const spaceWidth = ctx.measureText(' ').width + letterSpacingPx;
  const wordWidths = words.map((w) => measureLineWidth(ctx, w.text, letterSpacingPx));
  const totalWidth = wordWidths.reduce((sum, w) => sum + w, 0) + spaceWidth * Math.max(0, words.length - 1);
  const boxCenter = (boxLeft + boxRight) / 2;

  const startX = align === 'center' ? boxCenter - totalWidth / 2 : align === 'left' ? boxLeft : boxRight - totalWidth;

  let cursorX = startX;
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const scale = word.scale ?? 1;
    ctx.save();
    ctx.translate(cursorX + wordWidths[i] / 2, baselineY);
    ctx.scale(scale, scale);
    ctx.translate(-(cursorX + wordWidths[i] / 2), -baselineY);

    let charX = cursorX;
    for (const char of word.text) {
      if (outlineWidthPx > 0) {
        ctx.lineWidth = outlineWidthPx;
        ctx.strokeStyle = outlineColor;
        ctx.strokeText(char, charX, baselineY);
      }
      ctx.fillStyle = word.color ?? defaultColor;
      ctx.fillText(char, charX, baselineY);
      charX += ctx.measureText(char).width + letterSpacingPx;
    }
    ctx.restore();
    cursorX += wordWidths[i] + spaceWidth;
  }
}
